import { useState, useCallback, useMemo } from "react";
import { Button, message, Select, Space } from "antd";
import { CloseOutlined } from "@ant-design/icons";

import {
  FilterDropdownContainer,
  WhiteSelect,
} from "./styled";
import { useStores } from "@/hooks/use-stores";
import { defaultFilterCondition, fieldOptions } from "@/constants/Fallback";
import { FilterCondition } from "./types";
import { ConditionAction } from "@/constants/FallbackEnum";
import { getConditionLabelAsCompressorFailure, updateFilterCondition, validateFilters, handleConditionChange } from "./Fallbacks.utils";
import { useTranslation } from "react-i18next";

const { Option } = Select;

interface FilterDropdownProps {
  onClose: () => void;
  onApply: (filters: FilterCondition[]) => void;
}

const FilterDropdown = ({ onClose, onApply }: FilterDropdownProps) => {
  const { engineStore } = useStores();
  const { t } = useTranslation();
  const compressorOptions = useMemo(() => {
    return engineStore.applicableCompressorsForEngine.map((compressor) => ({
      label: compressor.name,
      value: compressor.id,
    }));
  }, [engineStore.applicableCompressorsForEngine]);
  const [conditions, setConditions] = useState<FilterCondition[]>([
    { ...defaultFilterCondition, label: "" },
  ]);

  const addCondition = useCallback(() => {
    setConditions([...conditions, { ...defaultFilterCondition, id: Date.now(), label: "" }]);
  }, [conditions]);
  
  const updateCondition = useCallback((
    id: number,
    key: keyof FilterCondition,
    val,
    label?: string
  ) => {
    setConditions((prev) => updateFilterCondition(prev, id, key, val, label, compressorOptions));
  }, [compressorOptions]);

  const removeCondition = useCallback((id: number) => {
    setConditions((prev) => prev.filter((cond) => cond.id !== id));
  }, []);

  const handleApply = useCallback(() => {
    const validationMessage = validateFilters(conditions);
    if (validationMessage !== "") {
      return message.error(t(validationMessage));
    }
    onApply(conditions.filter((c) => c.field));
    onClose();
  }, [conditions, t, onApply, onClose]);

  const shouldShowAddButton = Array.isArray(conditions) && conditions?.length > 0 && conditions[0]?.field && conditions?.length < fieldOptions.length;

  const handleConditionChangeWrapper = useCallback((cond: FilterCondition, action: ConditionAction, val) => {
    handleConditionChange(cond, action, val, updateCondition, removeCondition);
  }, [updateCondition, removeCondition]);
  
  const handleAddConditionClick = useCallback(() => {
    addCondition();
  }, [addCondition]);

  const conditionHandlers = useMemo(() => {
    return conditions.reduce((acc, cond) => {
      acc[`${cond.id}-${ConditionAction.Field}`] = (val: string) => handleConditionChangeWrapper(cond, ConditionAction.Field, val);
      acc[`${cond.id}-${ConditionAction.Value}`] = (val: string[]) => handleConditionChangeWrapper(cond, ConditionAction.Value, val);
      acc[`${cond.id}-${ConditionAction.Remove}`] = () => handleConditionChangeWrapper(cond, ConditionAction.Remove, null);
      return acc;
    }, {} as Record<string, (val?) => void>);
  }, [conditions, handleConditionChangeWrapper]);

  return (
    <FilterDropdownContainer>

      <div className="filter-header">
        <div className="filter-title">{t("filterFailScenario")}</div>
        <div className="filter-subtitle">
          {t("refineScenariosByKPIAndComponents")}
        </div>
      </div>

      <Space direction="vertical" style={{ width: "100%" }}>
        {conditions.map((cond, index) => (
          <div className="filter-condition-container" key={cond.id}>
            <div className="filter-condition-content">
              {index > 0 && (
                <div className="and-condition-container">
                  <div className="and-label">{t("AND")}</div>
                  <WhiteSelect
                    className="full-width-select"
                    placeholder={t("selectScenario")}
                    value={cond.field || undefined}
                    onChange={conditionHandlers[`${cond.id}-${ConditionAction.Field}`]}
                  >
                    {fieldOptions
                      .filter((opt) => !conditions.some((c) => c.field === opt.value))
                      .map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                  </WhiteSelect>
                </div>
              )}

              {index === 0 && (
                <WhiteSelect
                  className="full-width-select"
                  placeholder={t("selectScenario")}
                  value={cond.field || undefined}
                  onChange={conditionHandlers[`${cond.id}-${ConditionAction.Field}`]}
                >
                  {fieldOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </WhiteSelect>
              )}

              {getConditionLabelAsCompressorFailure(cond) && (
                <WhiteSelect
                  className="compressor-width-select"
                  mode="multiple"
                  placeholder={t("selectCompressors")}
                  value={cond.value as string[]}
                  onChange={conditionHandlers[`${cond.id}-${ConditionAction.Value}`]}
                >
                  {compressorOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </WhiteSelect>
              )}
            </div>

            {conditions.length > 1 && (
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={conditionHandlers[`${cond.id}-${ConditionAction.Remove}`]}
              />
            )}
          </div>
        ))}

        {shouldShowAddButton && (
          <div className="add-condition-button" onClick={handleAddConditionClick}>
            + {t("add")}
          </div>
        )}

        <Space className="filter-actions-container">
          <Button onClick={onClose}>{t("Cancel")}</Button>
          <Button
            type="primary"
            disabled={!conditions.some((c) => c.field)}
            onClick={handleApply}
          >
            {t("Filter")}
          </Button>
        </Space>
      </Space>
    </FilterDropdownContainer>
  );
};

export default FilterDropdown;
